import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Upload,
  Sparkles,
  FileText,
  Download,
  ArrowRight,
  Rocket
} from 'lucide-react';

interface WelcomeModalProps {
  open: boolean;
  onClose: () => void;
  userName?: string;
  siteName?: string;
}

export function WelcomeModal({ open, onClose, userName, siteName }: WelcomeModalProps) {
  const firstName = userName?.split(' ')[0] || 'there';

  const steps = [
    {
      icon: Upload,
      title: 'Upload Product Feed',
      description: 'Import your product catalog to get started',
      color: 'text-blue-500 bg-blue-100'
    },
    {
      icon: Sparkles,
      title: 'AI Enrichment',
      description: 'Let AI enhance your product data with attributes and benefits',
      color: 'text-purple-500 bg-purple-100'
    },
    {
      icon: FileText,
      title: 'Create AEO Content',
      description: 'Generate answer pages to boost SEO and help customers',
      color: 'text-green-500 bg-green-100'
    },
    {
      icon: Download,
      title: 'Export Enriched Data',
      description: 'Download your enhanced product feed for use anywhere',
      color: 'text-orange-500 bg-orange-100'
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
              <Rocket className="h-8 w-8 text-white" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl">
            Welcome to PageMind, {firstName}!
          </DialogTitle>
          <DialogDescription className="text-center text-base mt-2">
            {siteName ? (
              <>You're now a content manager for <strong>{siteName}</strong>.</>
            ) : (
              <>You're now a content manager.</>
            )}
            {' '}Here's how to get the most out of your dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <h4 className="text-sm font-semibold text-muted-foreground mb-4 text-center">
            YOUR CONTENT WORKFLOW
          </h4>
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div
                key={step.title}
                className="flex items-center gap-4 p-3 rounded-lg bg-muted/50"
              >
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                    {index + 1}
                  </span>
                  <div className={`p-2 rounded-lg ${step.color}`}>
                    <step.icon className="h-4 w-4" />
                  </div>
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-sm">{step.title}</h5>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="sm:justify-center">
          <Button onClick={onClose} size="lg" className="w-full sm:w-auto">
            Get Started
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
